import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import RefreshIcon from "@material-ui/icons/Refresh";
import SendIcon from "@material-ui/icons/Send";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  actions: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    marginBottom: theme.spacing(2),
  },
  hint: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
  chipApproved: { backgroundColor: "#c8e6c9" },
  chipPending: { backgroundColor: "#fff9c4" },
  chipRejected: { backgroundColor: "#ffcdd2" },
  mono: {
    fontFamily: "monospace",
    fontSize: 12,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  tableWrap: {
    overflowX: "auto",
  },
}));

const emptyCreate = {
  name: "",
  language: "pt_BR",
  category: "UTILITY",
  bodyText: "",
  headerText: "",
  footerText: "",
  buttonType: "",
  buttonText: "",
  buttonUrl: "",
};

const statusClass = (classes, status) => {
  const s = String(status || "").toUpperCase();
  if (s === "APPROVED") return classes.chipApproved;
  if (s === "REJECTED" || s === "DISABLED") return classes.chipRejected;
  return classes.chipPending;
};

const extractBodyText = template => {
  const comps = template?.components || [];
  const body = comps.find(c => String(c.type || "").toUpperCase() === "BODY");
  return body?.text || "";
};

const countPlaceholders = text => {
  const matches = String(text || "").match(/\{\{\d+\}\}/g) || [];
  const nums = matches.map(m => Number(m.replace(/\D/g, ""))).filter(Boolean);
  return nums.length ? Math.max(...nums) : 0;
};

const MetaTemplatesManager = ({
  showList = true,
  showSendPanel = true,
  autoLoad = true,
}) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [wabaId, setWabaId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyCreate);
  const [sendForm, setSendForm] = useState({
    to: "",
    name: "",
    language: "pt_BR",
    bodyParameters: "",
    headerParameter: "",
  });

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/waba/templates");
      setTemplates(data.templates || []);
      setWabaId(data.wabaId || null);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (autoLoad) loadTemplates();
  }, [autoLoad, loadTemplates]);

  const approvedTemplates = useMemo(
    () =>
      (templates || []).filter(
        t => String(t.status || "").toUpperCase() === "APPROVED"
      ),
    [templates]
  );

  const openCreate = (prefill = null) => {
    if (prefill) {
      setForm({
        ...emptyCreate,
        name: `${prefill.name || "copia"}_v2`.slice(0, 512),
        language: prefill.language || "pt_BR",
        category: prefill.category || "UTILITY",
        bodyText: extractBodyText(prefill),
        headerText:
          (prefill.components || []).find(
            c => String(c.type || "").toUpperCase() === "HEADER"
          )?.text || "",
        footerText:
          (prefill.components || []).find(
            c => String(c.type || "").toUpperCase() === "FOOTER"
          )?.text || "",
      });
    } else {
      setForm(emptyCreate);
    }
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        language: form.language,
        category: form.category,
        bodyText: form.bodyText,
        headerText: form.headerText || undefined,
        footerText: form.footerText || undefined,
      };
      if (form.buttonType && form.buttonText) {
        const btn = {
          type: form.buttonType,
          text: form.buttonText,
        };
        if (form.buttonType === "URL") btn.url = form.buttonUrl;
        payload.buttons = [btn];
      }
      await api.post("/waba/templates", payload);
      toast.success(
        "Template enviado para análise da Meta (status PENDING até aprovação)."
      );
      setCreateOpen(false);
      setForm(emptyCreate);
      await loadTemplates();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async template => {
    const ok = window.confirm(
      `Excluir o template "${template.name}" (${template.language})?\n\nTemplates aprovados não podem ser editados — só excluídos e recriados.`
    );
    if (!ok) return;
    try {
      await api.delete(`/waba/templates/${encodeURIComponent(template.name)}`, {
        params: template.id ? { hsmId: template.id } : undefined,
      });
      toast.success("Template excluído na Meta");
      await loadTemplates();
    } catch (err) {
      toastError(err);
    }
  };

  const selectForSend = template => {
    setSendForm(prev => ({
      ...prev,
      name: template.name,
      language: template.language,
    }));
  };

  const handleSend = async () => {
    setSending(true);
    try {
      const bodyParameters = String(sendForm.bodyParameters || "")
        .split("\n")
        .map(s => s.trim())
        .filter(Boolean);
      const { data } = await api.post("/waba/templates/send", {
        to: sendForm.to,
        name: sendForm.name,
        language: sendForm.language,
        bodyParameters: bodyParameters.length ? bodyParameters : undefined,
        headerParameter: sendForm.headerParameter || undefined,
      });
      const msgId = data?.result?.messages?.[0]?.id;
      toast.success(
        msgId
          ? `Template disparado. ID: ${msgId}`
          : "Template disparado com sucesso"
      );
    } catch (err) {
      toastError(err);
    } finally {
      setSending(false);
    }
  };

  const selectedBodyVars = useMemo(() => {
    const t = templates.find(
      x =>
        x.name === sendForm.name &&
        String(x.language) === String(sendForm.language)
    );
    return countPlaceholders(extractBodyText(t));
  }, [templates, sendForm.name, sendForm.language]);

  return (
    <Box>
      {showList ? (
        <>
          <Typography variant="body2" className={classes.hint}>
            Gerencie templates (textos de disparo) na Cloud API. A Meta analisa cada
            criação (PENDING → APPROVED/REJECTED). Templates aprovados não podem ser
            editados — use duplicar + excluir/recriar.
            {wabaId ? ` WABA: ${wabaId}` : ""}
          </Typography>

          <div className={classes.actions}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => openCreate()}
            >
              Novo template
            </Button>
            <Button
              variant="outlined"
              startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
              onClick={loadTemplates}
              disabled={loading}
            >
              Atualizar lista
            </Button>
          </div>

          <div className={classes.tableWrap}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell>Idioma</TableCell>
                  <TableCell>Categoria</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Corpo</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(templates || []).map(t => (
                  <TableRow key={`${t.id || t.name}-${t.language}`}>
                    <TableCell>{t.name}</TableCell>
                    <TableCell>{t.language}</TableCell>
                    <TableCell>{t.category}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={t.status || "—"}
                        className={statusClass(classes, t.status)}
                      />
                      {t.rejected_reason ? (
                        <Typography variant="caption" display="block" color="error">
                          {t.rejected_reason}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" className={classes.mono}>
                        {(extractBodyText(t) || "—").slice(0, 120)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {String(t.status || "").toUpperCase() === "APPROVED" &&
                      showSendPanel ? (
                        <IconButton
                          size="small"
                          title="Usar no disparo"
                          onClick={() => selectForSend(t)}
                        >
                          <SendIcon fontSize="small" />
                        </IconButton>
                      ) : null}
                      <IconButton
                        size="small"
                        title="Duplicar como novo (edição via recriação)"
                        onClick={() => openCreate(t)}
                      >
                        <FileCopyOutlinedIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        title="Excluir"
                        onClick={() => handleDelete(t)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {!loading && !templates.length ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography variant="body2" color="textSecondary">
                        Nenhum template encontrado. Crie um novo ou sincronize a
                        conta Meta.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </div>
        </>
      ) : null}

      {showSendPanel ? (
        <Box mt={showList ? 3 : 0}>
          {!showList ? (
            <div className={classes.actions}>
              <Button
                variant="outlined"
                startIcon={
                  loading ? <CircularProgress size={16} /> : <RefreshIcon />
                }
                onClick={loadTemplates}
                disabled={loading}
              >
                Atualizar templates aprovados
              </Button>
            </div>
          ) : null}
          <Typography variant="subtitle2" gutterBottom>
            Disparo de template (texto aprovado)
          </Typography>
          <Typography variant="caption" color="textSecondary" display="block" paragraph>
            Envia mensagem tipo template para um número (com DDI). Use variáveis
            do corpo uma por linha (na ordem {"{{1}}"}, {"{{2}}"}, …).
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                label="Número destino (DDI)"
                placeholder="5511999999999"
                value={sendForm.to}
                onChange={e =>
                  setSendForm(prev => ({ ...prev, to: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small" variant="outlined">
                <InputLabel>Template aprovado</InputLabel>
                <Select
                  label="Template aprovado"
                  value={
                    sendForm.name
                      ? `${sendForm.name}||${sendForm.language}`
                      : ""
                  }
                  onChange={e => {
                    const [name, language] = String(e.target.value).split("||");
                    setSendForm(prev => ({ ...prev, name, language }));
                  }}
                >
                  {approvedTemplates.map(t => (
                    <MenuItem
                      key={`${t.name}-${t.language}`}
                      value={`${t.name}||${t.language}`}
                    >
                      {t.name} ({t.language})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                label="Parâmetro do header (opcional)"
                value={sendForm.headerParameter}
                onChange={e =>
                  setSendForm(prev => ({
                    ...prev,
                    headerParameter: e.target.value,
                  }))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                multiline
                rows={Math.max(2, selectedBodyVars || 2)}
                label={
                  selectedBodyVars
                    ? `Variáveis do corpo (${selectedBodyVars} — uma por linha)`
                    : "Variáveis do corpo (opcional, uma por linha)"
                }
                value={sendForm.bodyParameters}
                onChange={e =>
                  setSendForm(prev => ({
                    ...prev,
                    bodyParameters: e.target.value,
                  }))
                }
              />
            </Grid>
          </Grid>
          <Box mt={2}>
            <Button
              variant="contained"
              color="primary"
              startIcon={
                sending ? <CircularProgress size={16} /> : <SendIcon />
              }
              disabled={sending || !sendForm.to || !sendForm.name}
              onClick={handleSend}
            >
              Disparar template
            </Button>
          </Box>
        </Box>
      ) : null}

      <Dialog
        open={createOpen}
        onClose={() => !saving && setCreateOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Novo template (Graph API)</DialogTitle>
        <DialogContent>
          <Typography variant="caption" color="textSecondary" paragraph>
            Nome só com letras minúsculas, números e _. Após criar, a Meta
            analisa o modelo. Edição de conteúdo aprovado não é permitida pela
            API — duplique e exclua o antigo se precisar alterar.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                label="Nome"
                value={form.name}
                onChange={e =>
                  setForm(prev => ({ ...prev, name: e.target.value }))
                }
                helperText="ex: confirmação_pedido"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                label="Idioma"
                value={form.language}
                onChange={e =>
                  setForm(prev => ({ ...prev, language: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small" variant="outlined">
                <InputLabel>Categoria</InputLabel>
                <Select
                  label="Categoria"
                  value={form.category}
                  onChange={e =>
                    setForm(prev => ({ ...prev, category: e.target.value }))
                  }
                >
                  <MenuItem value="UTILITY">UTILITY</MenuItem>
                  <MenuItem value="MARKETING">MARKETING</MenuItem>
                  <MenuItem value="AUTHENTICATION">AUTHENTICATION</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                label="Header (opcional)"
                value={form.headerText}
                onChange={e =>
                  setForm(prev => ({ ...prev, headerText: e.target.value }))
                }
                inputProps={{ maxLength: 60 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                multiline
                rows={4}
                label="Corpo (obrigatório)"
                value={form.bodyText}
                onChange={e =>
                  setForm(prev => ({ ...prev, bodyText: e.target.value }))
                }
                helperText='Use {{1}}, {{2}} para variáveis. Máx. 1024 caracteres.'
                inputProps={{ maxLength: 1024 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                label="Footer (opcional)"
                value={form.footerText}
                onChange={e =>
                  setForm(prev => ({ ...prev, footerText: e.target.value }))
                }
                inputProps={{ maxLength: 60 }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small" variant="outlined">
                <InputLabel>Botão (opcional)</InputLabel>
                <Select
                  label="Botão (opcional)"
                  value={form.buttonType}
                  onChange={e =>
                    setForm(prev => ({ ...prev, buttonType: e.target.value }))
                  }
                >
                  <MenuItem value="">Nenhum</MenuItem>
                  <MenuItem value="QUICK_REPLY">QUICK_REPLY</MenuItem>
                  <MenuItem value="URL">URL</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                label="Texto do botão"
                value={form.buttonText}
                disabled={!form.buttonType}
                onChange={e =>
                  setForm(prev => ({ ...prev, buttonText: e.target.value }))
                }
                inputProps={{ maxLength: 25 }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                label="URL do botão"
                value={form.buttonUrl}
                disabled={form.buttonType !== "URL"}
                onChange={e =>
                  setForm(prev => ({ ...prev, buttonUrl: e.target.value }))
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button
            color="primary"
            variant="contained"
            disabled={
              saving || !form.name.trim() || !form.bodyText.trim() || !form.language
            }
            onClick={handleCreate}
          >
            {saving ? "Enviando…" : "Criar e submeter"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MetaTemplatesManager;
